/**
 * Phase 3 — Sales Orders admin workspace.
 *
 * ONE file, three internal views (list / new / detail) routed via
 * query params so the whole feature lives in a single mount:
 *   /admin/sales-orders            → list
 *   /admin/sales-orders?view=new   → new (WhatsApp / manual-order workspace)
 *   /admin/sales-orders?id=<pid>   → detail
 *
 * The NewView is a THIN WRAPPER over the existing New-Sale/Quick-Sale
 * infrastructure. It reuses:
 *   • `AdminNewSaleHelpers.js` — emptyState / computeTotalsPreview /
 *      buildPayload (single source of truth for the mixed-cart shape,
 *      per-line taxable, per-line price overrides, and dollar /
 *      percentage sale discount).
 *   • `/api/orders/parse` + `/api/orders/quote` — the same sticker
 *      parser + pricing engine that New Sale uses; parsed preview
 *      populates BEFORE saving.
 *   • `/api/admin/customers/search-master` — the same master-customer
 *      resolver used by New Sale (name / email / phone / public id).
 *   • Shared Quick Items (`QuickItemsShared.jsx`).
 *
 * On save we POST to `/api/admin/sales-orders` (Sales Order write
 * path — order_type="sales_order", fulfillment/tracking lifecycle,
 * shipping_address snapshot, agreed_total adjustment). The backend
 * re-derives every total server-side; the FE preview is authoritative
 * only until save.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Search, DollarSign, Package, X, Trash2, User, Loader2,
} from "lucide-react";
import { QuickItemsPickerSheet, quickItemToCustomLine } from "./QuickItemsShared";
import { SalesOrderStatusBadge } from "../../components/admin/SalesOrderStatusBadge";
import {
  emptyState, computeTotalsPreview, buildPayload,
} from "./AdminNewSaleHelpers";


// Legacy fulfillment "fulfilled" is a historical catch-all. Phase-fix
// 2026-10 preserves it on disk when the intent is ambiguous, but
// displays truthful terminal statuses when the operator-captured
// fulfillment_intent proves which chain the order was on:
//   fulfilled + intent="pickup"   → "Picked Up"
//   fulfilled + intent="shipping" → "Shipped"
// Ambiguous rows (no intent) stay on the legacy "Fulfilled" badge.
function _displayFulfillment(order) {
  const v = order?.fulfillment_status || "unfulfilled";
  if (v === "fulfilled") {
    if (order?.fulfillment_intent === "pickup")   return "picked_up";
    if (order?.fulfillment_intent === "shipping") return "shipped";
  }
  return v;
}

// Returns the sole next-action for the pickup chain, or null when the
// chain is terminal / the order is on the shipping path.
function _pickupNextAction(order) {
  const intent = order?.fulfillment_intent;
  const cur    = order?.fulfillment_status || "unfulfilled";
  if (intent !== "pickup") return null;
  if (cur === "unfulfilled")  return "ready_pickup";
  if (cur === "ready_pickup") return "picked_up";
  return null;   // terminal (picked_up / fulfilled) — nothing to offer.
}

// Mark Shipped is only prominent on the shipping path AND when the
// order is still not shipped.
function _showMarkShippedAction(order) {
  if (order?.fulfillment_intent !== "shipping") return false;
  const cur = order?.fulfillment_status || "unfulfilled";
  return cur !== "shipped" && cur !== "fulfilled";
}


const PAYMENTS = [
  { key: "cash",    label: "Cash" },
  { key: "card",    label: "Card" },
  { key: "zelle",   label: "Zelle" },
  { key: "cashapp", label: "Cash App" },
  { key: "wise",    label: "WISE" },
  { key: "other",   label: "Other" },
];

const SOURCES = [
  { key: "in_person", label: "In-Person" },
  { key: "phone",     label: "Phone" },
  { key: "instagram", label: "Instagram" },
  { key: "whatsapp",  label: "WhatsApp" },
  { key: "facebook",  label: "Facebook" },
  { key: "tiktok",    label: "TikTok" },
  { key: "other",     label: "Other" },
];

const VARIANTS = [
  { value: "white",         label: "White" },
  { value: "blue",          label: "Blue" },
  { value: "gold_crumple",  label: "Gold" },
  { value: "red",           label: "Red" },
  { value: "orange",        label: "Orange" },
  { value: "purple",        label: "Purple" },
];

const FULFILLMENT_STATUSES = [
  "unfulfilled", "preparing", "ready_pickup", "ready_ship",
  "picked_up", "shipped", "fulfilled",
];

function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function useQuery() {
  const p = new URLSearchParams(window.location.search);
  return { view: p.get("view"), id: p.get("id"), edit: p.get("edit") };
}
function nav(qs) {
  window.history.pushState(null, "", `/admin/sales-orders${qs}`);
  window.dispatchEvent(new Event("popstate"));
}


// Phase-fix 2026-10 · Editability predicate — mirrors the backend
// gate in POST .../sales-orders/{id} exactly. Payment status alone
// must NEVER lock an open order.
export function isEditableOrder(order) {
  if (!order) return false;
  const so   = (order.sales_order_status || "open").toLowerCase();
  const fulf = (order.fulfillment_status || "unfulfilled").toLowerCase();
  if (so !== "open") return false;
  if (["picked_up", "shipped", "fulfilled"].includes(fulf)) return false;
  return true;
}


// Hydrate an edit-view state object from an existing Sales Order so
// the operator sees the current commercial content exactly as it is.
// The `preview` on each sticker list is pre-filled from the persisted
// snapshot so no re-parse is needed until the operator edits codes.
function hydrateStateFromOrder(order) {
  const snap  = order?.pricing_snapshot || {};
  const adj   = snap.adjustments        || {};
  const tax   = adj.taxability          || {};
  const disc  = adj.sale_discount;
  // Group sticker lines by variant.
  const byVar = {};
  for (const ln of (snap.lines || [])) {
    const v = (ln.variant || snap.variant || "white").toLowerCase();
    if (!byVar[v]) byVar[v] = [];
    byVar[v].push(ln);
  }
  const mkId = () => (crypto.randomUUID?.() || String(Math.random())).slice(0, 8);
  const stickerLists = Object.entries(byVar).map(([variant, lines]) => ({
    id:      mkId(),
    variant,
    rawText: lines.map((l) => (l.qty > 1 ? `${l.code} x${l.qty}` : l.code)).join("\n"),
    preview: { items:   lines.map((l) => ({ code: l.code, qty: l.qty })),
                snapshot: { lines, totals: snap.totals || {} },
                unknowns: [] },
  }));
  if (stickerLists.length === 0) {
    stickerLists.push({ id: mkId(), variant: "white", rawText: "", preview: null });
  }
  // Line overrides + per-line taxable use the FIRST sticker-list id as
  // the key prefix so the sticker-block rendering reads them correctly.
  const seedId = stickerLists[0].id;
  const lineOverrides = {};
  for (const o of (adj.line_price_overrides || [])) {
    lineOverrides[`${seedId}:${o.code}`] = {
      unit_price: o.unit_price, reason: o.reason || "admin_override",
    };
  }
  const perLineTaxable = {};
  for (const [code, v] of Object.entries(tax.per_line || {})) {
    perLineTaxable[`${seedId}:${code}`] = v;
  }
  const saleDiscount = (disc && (disc.amount > 0 || disc.value > 0))
    ? { kind: disc.kind || "fixed", value: disc.value || disc.amount }
    : { kind: "none", value: 0 };
  const totals = snap.totals || {};
  const shipObj = totals.shipping;
  const shippingAmount = (typeof shipObj === "object" && shipObj !== null)
    ? (shipObj.cost || 0) : (shipObj || 0);
  const manualAdj = totals.manual_adjustment || {};
  const agreedTotal = manualAdj.applied ? totals.total : "";
  return {
    state: {
      guest:    false,
      customer: order.customer || {},
      stickerLists,
      productLines: (snap.product_lines || []).map((p) => ({
        id: p.id, shopify_product_id: p.shopify_product_id,
        shopify_variant_id: p.shopify_variant_id, sku: p.sku,
        title: p.title, handle: p.handle, qty: p.qty,
        unit_price: p.unit_price, taxable: p.taxable !== false,
      })),
      customLines: (snap.custom_lines || []).map((c) => ({
        id: c.id, description: c.description, qty: c.qty,
        unit_price: c.unit_price, taxable: c.taxable !== false,
        ...(c.kind ? { kind: c.kind } : {}),
      })),
      lineOverrides, perLineTaxable,
      entireSaleNonTaxable: !!tax.entire_sale_non_taxable,
      saleDiscount, payments: [],
      source: order.source || "whatsapp",
      isHistorical: false, transactionDate: "",
      receiptPreference: "email",
      notesAdmin: order.notes_admin || "",
    },
    shippingAmount: String(shippingAmount || ""),
    agreedTotal:    String(agreedTotal    || ""),
  };
}


export default function AdminSalesOrders() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const q = useQuery(); void tick;
  if (q.id && q.edit === "1") return <EditView publicId={q.id}/>;
  if (q.id)             return <DetailView publicId={q.id}/>;
  if (q.view === "new") return <NewView/>;
  return <ListView/>;
}


// ══════════════════════════════════════════════════════════════
// List
// ══════════════════════════════════════════════════════════════
// Concise localized date for the list card — canonical Sales Order
// creation timestamp only. `updated_at` MUST NOT drive this display,
// so editing an older order never changes its list position OR the
// date shown on its card.
function formatOrderDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    }).replace(",", " ·");
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " · ");
  }
}

function ListView() {
  const [rows, setRows] = useState([]);
  // Default filter is "all" so the operator lands on a strictly
  // newest-created-first view across every sales order state.
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // `status="all"` → omit the query param so the backend returns
        // every sales order, still ordered by created_at DESC. Explicit
        // open/completed/cancelled are forwarded verbatim.
        const params = { limit: 100 };
        if (status && status !== "all") params.status = status;
        const r = await api.get("/admin/sales-orders", { params });
        if (alive) setRows(r.data?.results || []);
      } catch { if (alive) setRows([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [status]);

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-24"
         data-testid="sales-orders-list">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href="/admin" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Admin · Sales</div>
          <div className="font-bold">Sales Orders</div>
        </div>
        <a href="/admin/sales-orders?view=new"
           className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1"
           data-testid="sales-orders-new-btn">
          <Plus size={14}/> New
        </a>
      </div>
      {/* Centered content container — caps card width so wide desktops
          keep the order number, customer name, and totals close enough
          to scan in a single glance. Mobile stays fluid. */}
      <div className="mx-auto w-full max-w-[1120px] p-3 space-y-3"
           data-testid="sales-orders-container">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="border-2 border-[var(--ink)] rounded-xl px-3 py-2 bg-white text-sm"
                data-testid="sales-orders-status-filter">
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        {loading ? (
          <div className="text-sm text-[var(--smoke)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
               data-testid="sales-orders-empty">
            {status === "all" ? "No sales orders yet." : `No ${status} sales orders.`}
          </div>
        ) : (
          <div className="space-y-2" data-testid="sales-orders-rows">
            {rows.map((r) => (
              <a key={r.public_id} href={`/admin/sales-orders?id=${r.public_id}`}
                 className="block border-2 border-[var(--ink)] rounded-2xl p-3 bg-white active:bg-red-50"
                 data-testid={`sales-order-row-${r.public_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-base sm:text-lg truncate leading-tight"
                         data-testid={`sales-order-row-${r.public_id}-public-id`}>
                      {r.public_id}
                    </div>
                    <div className="font-bold text-base sm:text-lg truncate leading-tight text-[var(--ink)]"
                         data-testid={`sales-order-row-${r.public_id}-customer`}>
                      {r.customer?.name || "—"}
                    </div>
                    <div className="text-[11px] text-[var(--smoke)] mt-0.5"
                         data-testid={`sales-order-row-${r.public_id}-created-at`}>
                      {formatOrderDate(r.created_at)}
                    </div>
                  </div>
                  <div className="text-left sm:text-right sm:shrink-0">
                    <div className="text-base sm:text-lg font-black">
                      {money(r.pricing_snapshot?.totals?.total)}
                    </div>
                    {Number(r.balance_due || 0) > 0 && (
                      <div className="text-[11px] text-red-600 font-semibold">
                        Balance {money(r.balance_due)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center" data-testid={`sales-order-row-${r.public_id}-badges`}>
                  <SalesOrderStatusBadge value={r.payment_status || "unpaid"} kind="payment"/>
                  <SalesOrderStatusBadge value={_displayFulfillment(r)} kind="fulfillment"/>
                  {r.sales_order_status === "cancelled" && (
                    <SalesOrderStatusBadge value="cancelled" kind="order"/>
                  )}
                  <span className="bg-slate-100 text-slate-600 rounded px-2 py-0.5 text-[10px] uppercase">{r.source}</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// New — reuses the New-Sale helpers + parser
// ══════════════════════════════════════════════════════════════
function NewView() {
  const [state, setState] = useState(() => emptyState({ keepSource: "whatsapp" }));
  const [source, setSource] = useState("whatsapp");
  const [saving, setSaving] = useState(false);
  const [showQuickItems, setShowQuickItems] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [showAddShopify, setShowAddShopify] = useState(false);

  // Sales-Order-specific extensions layered on top of the reusable state.
  const [fulfillment, setFulfillment]   = useState("pickup");
  const [shippingAmount, setShippingAmount] = useState("");
  const [agreedTotal, setAgreedTotal]   = useState("");
  const [shipAddr, setShipAddr]         = useState({
    recipient_name: "", address_line_1: "", address_line_2: "",
    city: "", state_region: "", postal_code: "", country: "US",
  });

  // Phase 3 · Lifecycle emails — order confirmation opt-in. Defaults to
  // TRUE when the selected customer has an email; the checkbox is
  // disabled (and therefore effectively FALSE) when no email is on file.
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);
  const customerEmail = (state.customer.email || "").trim();
  const hasCustomerEmail = customerEmail.length > 0;

  // ── Sticker paste parser (mirrors AdminNewSale exactly) ─────────
  const listRefs = useRef({});
  const listTimers = useRef({});
  useEffect(() => {
    state.stickerLists.forEach((list) => {
      const currentRaw = list.rawText;
      const currentVar = list.variant;
      listRefs.current[list.id] = currentRaw;
      if (listTimers.current[list.id]) clearTimeout(listTimers.current[list.id]);
      if (!currentRaw || !currentRaw.trim()) {
        setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
          l.id === list.id ? { ...l, preview: null } : l) }));
        return;
      }
      listTimers.current[list.id] = setTimeout(async () => {
        try {
          const parsed = await api.post("/orders/parse", {
            album_slug: "panini-fifa-world-cup-2026",
            raw_text: currentRaw,
          });
          const items = (parsed.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
          let snapshot = null;
          if (items.length) {
            const quoted = await api.post("/orders/quote", {
              album_slug: "panini-fifa-world-cup-2026",
              items, delivery: { code: "local_pickup" },
              jurisdiction: "US-FL", variant: currentVar,
            });
            snapshot = quoted.data;
          }
          if (listRefs.current[list.id] !== currentRaw) return;
          setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
            l.id === list.id ? { ...l, preview: { items, snapshot,
                                                     unknowns: parsed.data?.unknowns || [] } } : l) }));
        } catch { /* silent — server owns totals */ }
      }, 350);
    });
    return () => {
      Object.values(listTimers.current).forEach((t) => t && clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.stickerLists.map((l) => `${l.id}|${l.variant}|${l.rawText}`).join("~")]);

  const totals = useMemo(() => computeTotalsPreview(state), [state]);

  const finalTotal = useMemo(() => {
    const shipping = Number(shippingAmount || 0);
    const calc = Math.round((totals.total + shipping) * 100) / 100;
    if (agreedTotal && Number(agreedTotal) > 0) {
      return Math.round(Number(agreedTotal) * 100) / 100;
    }
    return calc;
  }, [totals.total, shippingAmount, agreedTotal]);

  const hasAnyLine = state.stickerLists.some((l) => (l.preview?.items?.length || 0) > 0)
      || state.productLines.length > 0 || state.customLines.length > 0;
  const hasCustomer = state.customer.customer_id ||
    ((state.customer.first_name || state.customer.last_name)
      && (state.customer.email || state.customer.phone));
  const shippingOk = fulfillment === "pickup" ||
    (shipAddr.recipient_name.trim() && shipAddr.address_line_1.trim()
      && shipAddr.city.trim() && shipAddr.state_region.trim()
      && shipAddr.postal_code.trim());
  const canSave = hasAnyLine && hasCustomer && shippingOk && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      const base = buildPayload({ ...state, guest: false });
      const payload = {
        customer:            base.customer,
        album_slug:          base.album_slug,
        delivery:            base.delivery,
        jurisdiction:        base.jurisdiction,
        sticker_lists:       base.sticker_lists,
        product_lines:       base.product_lines,
        custom_lines:        base.custom_lines,
        line_price_overrides: base.line_price_overrides,
        taxability:          base.taxability,
        sale_discount:       base.sale_discount,
        source,
        fulfillment_intent:  fulfillment,
        shipping_amount:     Number(shippingAmount) || null,
        agreed_total:        Number(agreedTotal)    || null,
        shipping_address:    fulfillment === "shipping" ? shipAddr : null,
        initial_payments: (state.payments || [])
                              .filter((p) => Number(p.amount) > 0)
                              .map((p) => ({ amount: Number(p.amount),
                                                 method: p.method,
                                                 reference: p.reference || null })),
        notes: state.notesAdmin || undefined,
        // Phase 3 · Lifecycle emails — only sent when the checkbox is
        // ticked AND the backend confirms a customer email exists.
        send_confirmation_email: hasCustomerEmail && sendConfirmationEmail,
      };
      const r = await api.post("/admin/sales-orders", payload);
      if (r.data?.ok) {
        toast.success(`Sales Order ${r.data.public_id} created.`);
        nav(`?id=${r.data.public_id}`);
      } else {
        toast.error("Save failed.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(formatApiError(d) || (typeof d === "string" ? d : d?.message)
                    || "Save failed.");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-32"
         data-testid="sales-order-new">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href="/admin/sales-orders" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Admin · Sales</div>
          <div className="font-bold">New Sales Order</div>
        </div>
      </div>

      <div className="p-3 space-y-3 max-w-3xl mx-auto">
        <CustomerBlock state={state} setState={setState}/>

        <StickerListsBlock state={state} setState={setState}/>

        <ItemsBlock state={state} setState={setState}
                        onOpenQuick={() => setShowQuickItems(true)}
                        onOpenShopify={() => setShowAddShopify(true)}
                        onOpenCustom={() => setShowAddCustom(true)}/>

        <TaxDiscountBlock state={state} setState={setState}/>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Fulfillment</div>
          <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-xl p-1">
            <button onClick={() => setFulfillment("pickup")}
                    data-testid="sales-order-fulfillment-pickup"
                    className={`py-2 rounded-lg text-sm font-semibold ${fulfillment === "pickup" ? "bg-white shadow" : ""}`}>
              Pickup
            </button>
            <button onClick={() => setFulfillment("shipping")}
                    data-testid="sales-order-fulfillment-shipping"
                    className={`py-2 rounded-lg text-sm font-semibold ${fulfillment === "shipping" ? "bg-white shadow" : ""}`}>
              Shipping
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm">
              <span className="text-[10px] uppercase text-[var(--smoke)]">Shipping charge</span>
              <input type="number" min="0" step="0.01" value={shippingAmount}
                     onChange={(e) => setShippingAmount(e.target.value)}
                     placeholder="0.00"
                     className="mt-1 w-full border rounded-xl px-3 py-2 text-base"
                     data-testid="sales-order-shipping-amount"/>
            </label>
            <label className="text-sm">
              <span className="text-[10px] uppercase text-[var(--smoke)]">Agreed total (optional)</span>
              <input type="number" min="0" step="0.01" value={agreedTotal}
                     onChange={(e) => setAgreedTotal(e.target.value)}
                     placeholder="Auto"
                     className="mt-1 w-full border rounded-xl px-3 py-2 text-base"
                     data-testid="sales-order-agreed-total"/>
            </label>
          </div>
          {fulfillment === "shipping" && (
            <div className="pt-2 border-t space-y-2"
                 data-testid="sales-order-shipping-address-section">
              <div className="text-[10px] uppercase text-[var(--smoke)]">Ship-to Address</div>
              <input placeholder="Recipient name" value={shipAddr.recipient_name}
                     onChange={(e) => setShipAddr({ ...shipAddr, recipient_name: e.target.value })}
                     className="w-full border rounded-xl px-3 py-2 text-sm"
                     data-testid="sales-order-ship-recipient"/>
              <input placeholder="Address line 1" value={shipAddr.address_line_1}
                     onChange={(e) => setShipAddr({ ...shipAddr, address_line_1: e.target.value })}
                     className="w-full border rounded-xl px-3 py-2 text-sm"
                     data-testid="sales-order-ship-line1"/>
              <input placeholder="Address line 2 (optional)" value={shipAddr.address_line_2}
                     onChange={(e) => setShipAddr({ ...shipAddr, address_line_2: e.target.value })}
                     className="w-full border rounded-xl px-3 py-2 text-sm"/>
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="City" value={shipAddr.city}
                       onChange={(e) => setShipAddr({ ...shipAddr, city: e.target.value })}
                       className="border rounded-xl px-3 py-2 text-sm"
                       data-testid="sales-order-ship-city"/>
                <input placeholder="State" value={shipAddr.state_region}
                       onChange={(e) => setShipAddr({ ...shipAddr, state_region: e.target.value })}
                       className="border rounded-xl px-3 py-2 text-sm"
                       data-testid="sales-order-ship-state"/>
                <input placeholder="ZIP" value={shipAddr.postal_code}
                       onChange={(e) => setShipAddr({ ...shipAddr, postal_code: e.target.value })}
                       className="border rounded-xl px-3 py-2 text-sm"
                       data-testid="sales-order-ship-zip"/>
              </div>
            </div>
          )}
        </section>

        <PaymentsBlock state={state} setState={setState} finalTotal={finalTotal}/>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white">
          <label className="block text-sm">
            <span className="text-[10px] uppercase text-[var(--smoke)]">Source</span>
            <select value={source} onChange={(e) => setSource(e.target.value)}
                    className="mt-1 w-full border rounded-xl px-2 py-2 bg-white text-sm">
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="block text-sm mt-2">
            <span className="text-[10px] uppercase text-[var(--smoke)]">Notes</span>
            <textarea rows="2" value={state.notesAdmin}
                        onChange={(e) => setState((s) => ({ ...s, notesAdmin: e.target.value }))}
                        className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"/>
          </label>
        </section>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-1 text-sm"
                 data-testid="sales-order-totals">
          <TotalsRow label="Subtotal"  value={money(totals.subtotal)}/>
          {totals.discount > 0 && <TotalsRow label="Discount"  value={`− ${money(totals.discount)}`}/>}
          <TotalsRow label="Shipping"  value={money(shippingAmount || 0)}/>
          <TotalsRow label="Sales Tax" value={money(totals.tax)}/>
          {agreedTotal && Number(agreedTotal) > 0
            && Number(agreedTotal) !== Math.round((totals.total + Number(shippingAmount || 0)) * 100) / 100 && (
            <div className="text-[11px] text-[var(--panini-red)]"
                 data-testid="sales-order-adjustment-hint">
              Agreed total override — manual adjustment applied.
            </div>
          )}
          <div className="pt-2 mt-2 border-t flex items-center justify-between">
            <span className="font-bold">FINAL TOTAL</span>
            <b className="text-lg text-[var(--panini-red)]"
               data-testid="sales-order-final-total">{money(finalTotal)}</b>
          </div>
        </section>

        <button disabled={!canSave} onClick={save}
                data-testid="sales-order-save-btn"
                className={`w-full rounded-2xl py-3 font-bold text-white ${
                  canSave ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"
                }`}>
          {saving ? "Saving…" : "Create Sales Order"}
        </button>

        <label className="flex items-center gap-2 text-sm px-1 select-none"
               data-testid="sales-order-send-confirmation-label">
          <input type="checkbox"
                 checked={hasCustomerEmail && sendConfirmationEmail}
                 disabled={!hasCustomerEmail}
                 onChange={(e) => setSendConfirmationEmail(e.target.checked)}
                 data-testid="sales-order-send-confirmation-checkbox"
                 className="w-4 h-4"/>
          <span>
            Email order confirmation to customer
            {hasCustomerEmail
              ? <span className="text-[11px] text-[var(--smoke)] ml-1">
                  ({customerEmail})
                </span>
              : <span className="text-[11px] text-[var(--smoke)] ml-1">
                  (no email on file)
                </span>}
          </span>
        </label>
      </div>

      {showQuickItems && (
        <QuickItemsPickerSheet
          testidPrefix="sales-order-quick-items"
          onClose={() => setShowQuickItems(false)}
          onPick={(item, cfg) => {
            setState((s) => ({ ...s, customLines: [...s.customLines,
                                                        quickItemToCustomLine(item, cfg)] }));
            setShowQuickItems(false);
            toast.success(`${item.name} added.`, { duration: 1200 });
          }}/>
      )}
      {showAddShopify && (
        <ShopifyPickerModal onClose={() => setShowAddShopify(false)}
                                  onAdd={(l) => {
                                    setState((s) => ({ ...s, productLines: [...s.productLines, l] }));
                                    setShowAddShopify(false);
                                  }}/>
      )}
      {showAddCustom && (
        <CustomLineModal onClose={() => setShowAddCustom(false)}
                             onAdd={(l) => {
                               setState((s) => ({ ...s, customLines: [...s.customLines, l] }));
                               setShowAddCustom(false);
                             }}/>
      )}
    </div>
  );
}


// ─── Customer block — reuses master-customer search endpoint ─────
function CustomerBlock({ state, setState }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/admin/customers/search-master",
                                  { params: { q, limit: 8 } });
        setResults(r.data?.results || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  function pick(row) {
    setState((s) => ({ ...s, guest: false, customer: {
      customer_id: row.id || null,
      first_name: row.first_name || "",
      last_name:  row.last_name  || "",
      email:      row.email      || "",
      phone:      row.phone      || "",
    }}));
    setQ(""); setResults([]);
    toast.success("Customer attached.");
  }

  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
             data-testid="sales-order-customer-block">
      <div className="flex items-center gap-2 mb-2">
        <User size={16} className="text-[var(--panini-red)]"/>
        <h3 className="font-bold text-sm">Customer</h3>
      </div>
      <div className="relative">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by name, email, phone, or CU-…"
               className="w-full border rounded-xl px-3 py-2 pr-9 text-sm"
               data-testid="sales-order-customer-search"/>
        <div className="absolute right-3 top-2.5">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Search size={14}/>}
        </div>
        {results.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full bg-white border-2 border-[var(--ink)] rounded-lg max-h-64 overflow-y-auto shadow"
              data-testid="sales-order-customer-results">
            {results.map((r, i) => (
              <li key={r.id || i}>
                <button type="button" onClick={() => pick(r)}
                        className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-gray-100 last:border-0"
                        data-testid={`sales-order-customer-result-${i}`}>
                  <div className="font-semibold text-sm">
                    {r.display_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email}
                  </div>
                  <div className="text-xs text-[var(--smoke)]">
                    {r.email || "—"} · {r.phone || "—"}
                    {r.public_customer_id ? ` · ${r.public_customer_id}` : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
        <input placeholder="First name" value={state.customer.first_name}
               onChange={(e) => setState((s) => ({ ...s, customer:
                     { ...s.customer, first_name: e.target.value, customer_id: null }}))}
               className="border rounded-lg px-2 py-1"
               data-testid="sales-order-customer-first"/>
        <input placeholder="Last name" value={state.customer.last_name}
               onChange={(e) => setState((s) => ({ ...s, customer:
                     { ...s.customer, last_name: e.target.value, customer_id: null }}))}
               className="border rounded-lg px-2 py-1"
               data-testid="sales-order-customer-last"/>
        <input placeholder="Email" value={state.customer.email}
               onChange={(e) => setState((s) => ({ ...s, customer:
                     { ...s.customer, email: e.target.value, customer_id: null }}))}
               className="border rounded-lg px-2 py-1"
               data-testid="sales-order-customer-email"/>
        <input placeholder="Phone" value={state.customer.phone}
               onChange={(e) => setState((s) => ({ ...s, customer:
                     { ...s.customer, phone: e.target.value, customer_id: null }}))}
               className="border rounded-lg px-2 py-1"
               data-testid="sales-order-customer-phone"/>
      </div>
      {state.customer.customer_id && (
        <div className="text-[11px] text-emerald-700 mt-1"
             data-testid="sales-order-customer-linked">
          Linked to master customer.
        </div>
      )}
    </section>
  );
}


// ─── Sticker lists block — paste raw text + variant + preview ────
function StickerListsBlock({ state, setState }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-3"
             data-testid="sales-order-sticker-block">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm">Single Stickers</h3>
        <button onClick={() => setState((s) => ({ ...s, stickerLists: [...s.stickerLists,
                                                          { id: (crypto.randomUUID?.() || String(Math.random())).slice(0, 8),
                                                            variant: "white", rawText: "", preview: null }] }))}
                className="text-[11px] font-semibold text-[var(--panini-red)]">
          + Add List
        </button>
      </div>
      {state.stickerLists.map((list) => (
        <div key={list.id} className="border rounded-xl p-2 space-y-2">
          <div className="flex items-center gap-2">
            <select value={list.variant}
                    onChange={(e) => setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
                      l.id === list.id ? { ...l, variant: e.target.value } : l) }))}
                    className="border rounded-lg px-2 py-1 bg-white text-sm"
                    data-testid="sales-order-sticker-variant">
              {VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
            {state.stickerLists.length > 1 && (
              <button onClick={() => setState((s) => ({ ...s, stickerLists:
                                                                s.stickerLists.filter((l) => l.id !== list.id) }))}
                      className="ml-auto p-1 text-[var(--smoke)]"><Trash2 size={14}/></button>
            )}
          </div>
          <textarea rows="3" value={list.rawText}
                     onChange={(e) => setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
                       l.id === list.id ? { ...l, rawText: e.target.value } : l) }))}
                     placeholder="Paste codes e.g. ARG17, BRA19, POR15, FWC3, CC1"
                     className="w-full border rounded-lg px-2 py-2 text-sm font-mono"
                     data-testid="sales-order-sticker-raw"/>
          {list.preview?.snapshot?.lines && (
            <div className="text-xs border-t pt-2" data-testid="sales-order-sticker-parsed">
              <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">
                Parsed · {list.preview.snapshot.lines.length} line(s)
              </div>
              <div className="divide-y">
                {list.preview.snapshot.lines.map((ln, i) => {
                  const key = `${list.id}:${ln.code}`;
                  const eff = state.lineOverrides[key]?.unit_price ?? ln.unit_price;
                  const isTax = state.perLineTaxable[key] !== false;
                  return (
                    <div key={i} className="py-1 grid grid-cols-[1fr_60px_80px_28px] gap-1 items-center">
                      <span className="font-mono text-[11px]">{ln.code} × {ln.qty}</span>
                      <NumericInput value={eff}
                                       step="0.01"
                                       onChange={(v) => setState((s) => ({ ...s, lineOverrides:
                                             { ...s.lineOverrides,
                                               [key]: { unit_price: v, reason: "admin_override" }}}))}
                                       testid={`sales-order-sticker-price-${ln.code}`}/>
                      <div className="text-right font-mono text-xs">
                        {money(eff * ln.qty)}
                      </div>
                      <label className="text-[10px] flex items-center justify-center">
                        <input type="checkbox" checked={isTax}
                               onChange={(e) => setState((s) => ({ ...s, perLineTaxable:
                                     { ...s.perLineTaxable, [key]: e.target.checked }}))}
                               data-testid={`sales-order-sticker-taxable-${ln.code}`}/>
                      </label>
                    </div>
                  );
                })}
              </div>
              {(list.preview.unknowns || []).length > 0 && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Unrecognized: {list.preview.unknowns.map((u) => u.code || u).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}


// ─── Items block — Shopify + Quick Items + Custom ─────────────────
function ItemsBlock({ state, setState, onOpenQuick, onOpenShopify, onOpenCustom }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-3"
             data-testid="sales-order-items-block">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm">Items</h3>
        <div className="flex gap-1">
          <button onClick={onOpenShopify}
                  className="text-[11px] font-semibold border rounded-full px-2 py-1"
                  data-testid="sales-order-add-shopify-btn">+ Shopify</button>
          <button onClick={onOpenQuick}
                  className="text-[11px] font-semibold border rounded-full px-2 py-1"
                  data-testid="sales-order-add-quick-btn">+ Quick Item</button>
          <button onClick={onOpenCustom}
                  className="text-[11px] font-semibold border rounded-full px-2 py-1"
                  data-testid="sales-order-add-custom-btn">+ Custom</button>
        </div>
      </div>
      {state.productLines.map((p, i) => (
        <EditableLine key={`p${i}`} label={p.title || `#${i + 1}`}
                          qty={p.qty} price={p.unit_price} taxable={p.taxable !== false}
                          onQty={(v) => setState((s) => ({ ...s, productLines:
                                    s.productLines.map((x, j) => j === i ? { ...x, qty: v } : x) }))}
                          onPrice={(v) => setState((s) => ({ ...s, productLines:
                                       s.productLines.map((x, j) => j === i ? { ...x, unit_price: v } : x) }))}
                          onTaxable={(v) => setState((s) => ({ ...s, productLines:
                                         s.productLines.map((x, j) => j === i ? { ...x, taxable: v } : x) }))}
                          onRemove={() => setState((s) => ({ ...s, productLines:
                                          s.productLines.filter((_, j) => j !== i) }))}
                          testidPrefix={`sales-order-product-${i}`}/>
      ))}
      {state.customLines.map((c, i) => (
        <EditableLine key={`c${i}`} label={c.description || c.historical_quick_item_name || `Custom ${i + 1}`}
                          qty={c.qty} price={c.unit_price} taxable={c.taxable !== false}
                          onQty={(v) => setState((s) => ({ ...s, customLines:
                                    s.customLines.map((x, j) => j === i ? { ...x, qty: v } : x) }))}
                          onPrice={(v) => setState((s) => ({ ...s, customLines:
                                       s.customLines.map((x, j) => j === i ? { ...x, unit_price: v } : x) }))}
                          onTaxable={(v) => setState((s) => ({ ...s, customLines:
                                         s.customLines.map((x, j) => j === i ? { ...x, taxable: v } : x) }))}
                          onRemove={() => setState((s) => ({ ...s, customLines:
                                          s.customLines.filter((_, j) => j !== i) }))}
                          testidPrefix={`sales-order-custom-${i}`}/>
      ))}
      {state.productLines.length === 0 && state.customLines.length === 0 && (
        <div className="text-[11px] text-[var(--smoke)]">
          No items yet — add stickers above, Shopify products, Quick Items, or custom lines.
        </div>
      )}
    </section>
  );
}


function EditableLine({ label, qty, price, taxable, onQty, onPrice, onTaxable, onRemove, testidPrefix }) {
  return (
    <div className="border rounded-xl p-2 grid grid-cols-[1fr_56px_72px_28px_24px] gap-1 items-center text-sm">
      <div className="min-w-0 truncate">{label}</div>
      <NumericInput value={qty} step="1" min="1" integer
                       onChange={onQty} testid={`${testidPrefix}-qty`}/>
      <NumericInput value={price} step="0.01"
                       onChange={onPrice} testid={`${testidPrefix}-price`}/>
      <label className="flex items-center justify-center text-[10px]">
        <input type="checkbox" checked={taxable} onChange={(e) => onTaxable(e.target.checked)}
               data-testid={`${testidPrefix}-taxable`}/>
      </label>
      <button onClick={onRemove} className="text-[var(--smoke)]"
              data-testid={`${testidPrefix}-remove`}><Trash2 size={14}/></button>
    </div>
  );
}


// Text-based numeric input that ALLOWS an empty transient state so
// the operator can select the "1" and type "5" without the field
// snapping back. Validates only on blur.
function NumericInput({ value, onChange, step, min, integer, testid }) {
  const [local, setLocal] = useState(String(value ?? ""));
  useEffect(() => { setLocal(String(value ?? "")); }, [value]);
  return (
    <input type="number" step={step} min={min} inputMode={integer ? "numeric" : "decimal"}
           value={local}
           onChange={(e) => setLocal(e.target.value)}
           onBlur={() => {
             if (local === "" || local === "-") {
               const fallback = integer ? Math.max(1, Number(min || 1)) : 0;
               setLocal(String(fallback));
               onChange(fallback);
               return;
             }
             let n = integer ? Math.floor(Number(local) || 0) : Number(local) || 0;
             if (integer) n = Math.max(Number(min || 1), n);
             if (!integer) n = Math.round(n * 100) / 100;
             setLocal(String(n));
             onChange(n);
           }}
           className="border rounded-lg px-2 py-1 text-xs w-full font-mono"
           data-testid={testid}/>
  );
}


// ─── Tax + Discount block ────────────────────────────────────────
function TaxDiscountBlock({ state, setState }) {
  const chargeTax = !state.entireSaleNonTaxable;
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2"
             data-testid="sales-order-tax-discount-block">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={chargeTax}
               onChange={(e) => setState((s) => ({ ...s, entireSaleNonTaxable: !e.target.checked }))}
               data-testid="sales-order-charge-tax"/>
        <span>Charge Sales Tax</span>
      </label>
      <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
        <select value={state.saleDiscount.kind}
                onChange={(e) => setState((s) => ({ ...s, saleDiscount:
                    { kind: e.target.value, value: e.target.value === "none" ? 0 : s.saleDiscount.value }}))}
                className="border rounded-xl px-2 py-2 bg-white"
                data-testid="sales-order-discount-kind">
          <option value="none">No Discount</option>
          <option value="fixed">$ Discount</option>
          <option value="percentage">% Discount</option>
        </select>
        {state.saleDiscount.kind !== "none" && (
          <input type="number" min="0" step="0.01" value={state.saleDiscount.value}
                 onChange={(e) => setState((s) => ({ ...s, saleDiscount:
                       { ...s.saleDiscount, value: Number(e.target.value) || 0 }}))}
                 placeholder={state.saleDiscount.kind === "percentage" ? "%" : "$"}
                 className="border rounded-xl px-3 py-2"
                 data-testid="sales-order-discount-value"/>
        )}
      </div>
    </section>
  );
}


// ─── Payments block ──────────────────────────────────────────────
function PaymentsBlock({ state, setState, finalTotal }) {
  const paid = (state.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const balance = Math.max(0, Math.round((finalTotal - paid) * 100) / 100);
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2"
             data-testid="sales-order-payments-block">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm">Payments</h3>
        <button onClick={() => setState((s) => ({ ...s, payments: [...s.payments,
                                                      { amount: "", method: "cash", reference: "" }] }))}
                className="text-[11px] font-semibold text-[var(--panini-red)]"
                data-testid="sales-order-add-payment-row">
          + Add Payment
        </button>
      </div>
      {(state.payments || []).map((p, i) => (
        <div key={i} className="grid grid-cols-[80px_1fr_24px] gap-1 items-center">
          <input type="number" min="0" step="0.01" value={p.amount}
                 onChange={(e) => setState((s) => ({ ...s, payments: s.payments.map((x, j) =>
                     j === i ? { ...x, amount: e.target.value } : x) }))}
                 placeholder="0.00"
                 className="border rounded-lg px-2 py-1 text-sm"
                 data-testid={`sales-order-payment-${i}-amount`}/>
          <select value={p.method}
                  onChange={(e) => setState((s) => ({ ...s, payments: s.payments.map((x, j) =>
                      j === i ? { ...x, method: e.target.value } : x) }))}
                  className="border rounded-lg px-2 py-1 bg-white text-sm"
                  data-testid={`sales-order-payment-${i}-method`}>
            {PAYMENTS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <button onClick={() => setState((s) => ({ ...s, payments:
                                                        s.payments.filter((_, j) => j !== i) }))}
                  className="text-[var(--smoke)]"><Trash2 size={14}/></button>
        </div>
      ))}
      <div className="flex items-center justify-between text-xs pt-2 border-t">
        <span>Balance Due</span>
        <b className="text-red-600" data-testid="sales-order-balance-preview">
          {money(balance)}
        </b>
      </div>
    </section>
  );
}


function TotalsRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}


// ─── Shopify picker (compact) ────────────────────────────────────
function ShopifyPickerModal({ onClose, onAdd }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/shopify-products/search",
                                    { params: { q, limit: 20 } });
        if (alive) setRows(r.data?.results || []);
      } catch { if (alive) setRows([]); }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid="sales-order-shopify-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-3 space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div className="font-bold text-sm">Add Shopify Product</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 text-[var(--smoke)]" size={14}/>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="title / SKU"
                 className="w-full border rounded-xl pl-7 pr-3 py-2 text-sm"/>
        </div>
        <div className="border rounded-xl divide-y max-h-72 overflow-auto">
          {rows.map((p, i) => (
            <button key={p.id || i}
                    onClick={() => onAdd({
                      id: p.id, shopify_product_id: p.shopify_product_id,
                      shopify_variant_id: p.shopify_variant_id || undefined,
                      sku: p.sku, title: p.title,
                      qty: 1, unit_price: Number(p.unit_price || 0),
                      taxable: p.taxable !== false,
                    })}
                    className="w-full text-left p-2 flex items-center gap-2 text-sm active:bg-red-50">
              <div className="w-8 h-8 flex-shrink-0">
                {p.image_url ? <img src={p.image_url} className="w-8 h-8 object-contain"/> : <Package size={16}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.title}</div>
                <div className="text-[10px] text-[var(--smoke)]">
                  {money(p.unit_price)} {p.sku ? `· ${p.sku}` : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Custom line modal ───────────────────────────────────────────
function CustomLineModal({ onClose, onAdd }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");
  const [taxable, setTaxable] = useState(true);
  const canSave = name.trim() && Number(price) > 0 && Number(qty) >= 1;
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid="sales-order-custom-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-bold text-sm">Custom Line</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Description"
               className="w-full border rounded-xl px-3 py-2 text-sm"
               data-testid="sales-order-custom-name"/>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="1" step="1" inputMode="numeric" value={qty}
                 onChange={(e) => setQty(e.target.value)}
                 placeholder="Qty"
                 className="border rounded-xl px-3 py-2 text-sm"
                 data-testid="sales-order-custom-qty"/>
          <input type="number" min="0" step="0.01" inputMode="decimal" value={price}
                 onChange={(e) => setPrice(e.target.value)}
                 placeholder="Unit price"
                 className="border rounded-xl px-3 py-2 text-sm"
                 data-testid="sales-order-custom-price"/>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={taxable}
                 onChange={(e) => setTaxable(e.target.checked)}
                 data-testid="sales-order-custom-taxable"/>
          Taxable
        </label>
        <button disabled={!canSave}
                onClick={() => {
                  onAdd({
                    id: (crypto.randomUUID?.() || String(Math.random())).slice(0, 8),
                    description: name.trim(),
                    qty: Math.max(1, Math.floor(Number(qty)) || 1),
                    unit_price: Number(price),
                    taxable,
                  });
                }}
                data-testid="sales-order-custom-save"
                className={`w-full rounded-2xl py-3 font-bold text-white ${
                  canSave ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"
                }`}>
          Add to Sale
        </button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Edit — reuses the New-Sale composition for an OPEN Sales Order
// ══════════════════════════════════════════════════════════════
function EditView({ publicId }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [shippingAmount, setShippingAmount] = useState("");
  const [agreedTotal, setAgreedTotal] = useState("");
  const [showQuickItems, setShowQuickItems] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [showAddShopify, setShowAddShopify] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/admin/sales-orders/${publicId}`);
        if (!alive) return;
        setOrder(r.data || null);
        if (r.data && isEditableOrder(r.data)) {
          const h = hydrateStateFromOrder(r.data);
          setState(h.state);
          setShippingAmount(h.shippingAmount);
          setAgreedTotal(h.agreedTotal);
        }
      } catch { if (alive) setOrder(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [publicId]);

  // Live sticker re-parse on textarea edits — same debounced path as
  // NewView so the operator sees updated line pricing before saving.
  const listRefs = useRef({});
  const listTimers = useRef({});
  useEffect(() => {
    if (!state) return;
    state.stickerLists.forEach((list) => {
      const currentRaw = list.rawText;
      const currentVar = list.variant;
      listRefs.current[list.id] = currentRaw;
      if (listTimers.current[list.id]) clearTimeout(listTimers.current[list.id]);
      if (!currentRaw || !currentRaw.trim()) {
        setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
          l.id === list.id ? { ...l, preview: null } : l) }));
        return;
      }
      listTimers.current[list.id] = setTimeout(async () => {
        try {
          const parsed = await api.post("/orders/parse", {
            album_slug: "panini-fifa-world-cup-2026", raw_text: currentRaw });
          const items = (parsed.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
          let snapshot = null;
          if (items.length) {
            const quoted = await api.post("/orders/quote", {
              album_slug: "panini-fifa-world-cup-2026",
              items, delivery: { code: "local_pickup" },
              jurisdiction: "US-FL", variant: currentVar });
            snapshot = quoted.data;
          }
          if (listRefs.current[list.id] !== currentRaw) return;
          setState((s) => ({ ...s, stickerLists: s.stickerLists.map((l) =>
            l.id === list.id ? { ...l, preview: { items, snapshot,
                                                     unknowns: parsed.data?.unknowns || [] } } : l) }));
        } catch { /* server owns totals */ }
      }, 350);
    });
    return () => {
      Object.values(listTimers.current).forEach((t) => t && clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state && state.stickerLists.map((l) => `${l.id}|${l.variant}|${l.rawText}`).join("~")]);

  const totals = useMemo(() => state ? computeTotalsPreview(state) : null, [state]);
  const finalTotal = useMemo(() => {
    if (!totals) return 0;
    const shipping = Number(shippingAmount || 0);
    const calc = Math.round((totals.total + shipping) * 100) / 100;
    if (agreedTotal && Number(agreedTotal) > 0) {
      return Math.round(Number(agreedTotal) * 100) / 100;
    }
    return calc;
  }, [totals, shippingAmount, agreedTotal]);

  async function save() {
    if (!state || !order || saving) return;
    setSaving(true);
    try {
      const base = buildPayload({ ...state, guest: false });
      const payload = {
        expected_updated_at:  order.updated_at,
        album_slug:           base.album_slug,
        delivery:             base.delivery,
        jurisdiction:         base.jurisdiction,
        sticker_lists:        base.sticker_lists,
        product_lines:        base.product_lines,
        custom_lines:         base.custom_lines,
        line_price_overrides: base.line_price_overrides,
        taxability:           base.taxability,
        sale_discount:        base.sale_discount,
        shipping_amount:      Number(shippingAmount) || null,
        agreed_total:         Number(agreedTotal) || null,
        shipping_address:     order.fulfillment_intent === "shipping"
                                ? (order.shipping?.address || null) : null,
      };
      const r = await api.patch(`/admin/sales-orders/${publicId}`, payload);
      if (r.data?.ok) {
        toast.success("Order updated.");
        nav(`?id=${publicId}`);
      } else {
        toast.error("Save failed.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      if (e?.response?.status === 409 || code === "stale_edit") {
        toast.error("Order changed since you opened it. Reload before saving.",
                    { duration: 4000 });
      } else if (code === "order_not_editable") {
        toast.error("This order is no longer editable.");
        nav(`?id=${publicId}`);
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Save failed.");
      }
    } finally { setSaving(false); }
  }

  if (loading) return <div className="p-6 text-sm text-[var(--smoke)]">Loading…</div>;
  if (!order)  return <div className="p-6 text-sm text-red-600">Order not found.</div>;
  if (!isEditableOrder(order) || !state) {
    return (
      <div className="min-h-screen bg-[var(--paper,_#fafaf7)] p-3"
           data-testid="sales-order-edit-blocked">
        <div className="max-w-md mx-auto border-2 border-[var(--ink)] rounded-2xl p-4 bg-white space-y-2">
          <div className="font-bold text-sm">This order is not editable.</div>
          <div className="text-xs text-[var(--smoke)]">
            Status: {order.sales_order_status} · Fulfillment: {order.fulfillment_status}
          </div>
          <a href={`/admin/sales-orders?id=${publicId}`}
             className="inline-block text-sm font-semibold text-[var(--panini-red)]"
             data-testid="sales-order-edit-back-btn">← Back to order</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-32"
         data-testid="sales-order-edit">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href={`/admin/sales-orders?id=${publicId}`} className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Admin · Sales</div>
          <div className="font-bold truncate">Edit {order.public_id}</div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1120px] p-3 space-y-3">
        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                 data-testid="sales-order-edit-customer-readonly">
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Customer (not editable)</div>
          <div className="font-semibold">{order.customer?.name || "—"}</div>
          <div className="text-xs text-[var(--smoke)]">
            {order.customer?.email || order.customer?.phone}
          </div>
        </section>

        <StickerListsBlock state={state} setState={setState}/>
        <ItemsBlock state={state} setState={setState}
                        onOpenQuick={() => setShowQuickItems(true)}
                        onOpenShopify={() => setShowAddShopify(true)}
                        onOpenCustom={() => setShowAddCustom(true)}/>
        <TaxDiscountBlock state={state} setState={setState}/>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Shipping & Adjustments</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm">
              <span className="text-[10px] uppercase text-[var(--smoke)]">Shipping charge</span>
              <input type="number" min="0" step="0.01" value={shippingAmount}
                     onChange={(e) => setShippingAmount(e.target.value)}
                     placeholder="0.00"
                     className="mt-1 w-full border rounded-xl px-3 py-2 text-base"
                     data-testid="sales-order-edit-shipping-amount"/>
            </label>
            <label className="text-sm">
              <span className="text-[10px] uppercase text-[var(--smoke)]">Agreed total (optional)</span>
              <input type="number" min="0" step="0.01" value={agreedTotal}
                     onChange={(e) => setAgreedTotal(e.target.value)}
                     placeholder="Auto"
                     className="mt-1 w-full border rounded-xl px-3 py-2 text-base"
                     data-testid="sales-order-edit-agreed-total"/>
            </label>
          </div>
          <div className="text-[11px] text-[var(--smoke)]">
            Fulfillment intent locked for editing: <b>{order.fulfillment_intent}</b>
          </div>
        </section>

        {totals && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-1 text-sm"
                   data-testid="sales-order-edit-totals">
            <TotalsRow label="Subtotal"  value={money(totals.subtotal)}/>
            {totals.discount > 0 && <TotalsRow label="Discount"  value={`− ${money(totals.discount)}`}/>}
            <TotalsRow label="Shipping"  value={money(shippingAmount || 0)}/>
            <TotalsRow label="Sales Tax" value={money(totals.tax)}/>
            <div className="pt-2 mt-2 border-t flex items-center justify-between">
              <span className="font-bold">NEW TOTAL</span>
              <b className="text-lg text-[var(--panini-red)]"
                 data-testid="sales-order-edit-final-total">{money(finalTotal)}</b>
            </div>
          </section>
        )}

        <div className="grid grid-cols-2 gap-2">
          <a href={`/admin/sales-orders?id=${publicId}`}
             data-testid="sales-order-edit-cancel-btn"
             className="text-center rounded-2xl py-3 font-bold bg-slate-200 text-slate-800">
            Cancel Editing
          </a>
          <button disabled={saving} onClick={save}
                  data-testid="sales-order-edit-save-btn"
                  className={`rounded-2xl py-3 font-bold text-white ${
                    saving ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                  }`}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {showQuickItems && (
        <QuickItemsPickerSheet testidPrefix="sales-order-edit-quick-items"
          onClose={() => setShowQuickItems(false)}
          onPick={(item, cfg) => {
            setState((s) => ({ ...s, customLines: [...s.customLines,
                                                        quickItemToCustomLine(item, cfg)] }));
            setShowQuickItems(false);
            toast.success(`${item.name} added.`, { duration: 1200 });
          }}/>
      )}
      {showAddShopify && (
        <ShopifyPickerModal onClose={() => setShowAddShopify(false)}
                                  onAdd={(l) => {
                                    setState((s) => ({ ...s, productLines: [...s.productLines, l] }));
                                    setShowAddShopify(false);
                                  }}/>
      )}
      {showAddCustom && (
        <CustomLineModal onClose={() => setShowAddCustom(false)}
                             onAdd={(l) => {
                               setState((s) => ({ ...s, customLines: [...s.customLines, l] }));
                               setShowAddCustom(false);
                             }}/>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Detail (unchanged — carrier/tracking/completion)
// ══════════════════════════════════════════════════════════════
function DetailView({ publicId }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingPayment, setAddingPayment] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  // Phase 3 · Lifecycle emails — shipping-confirmation opt-in. Defaults
  // to TRUE; disabled when no customer email or pickup intent.
  const [sendShippingEmail, setSendShippingEmail] = useState(true);

  async function reload() {
    try {
      const r = await api.get(`/admin/sales-orders/${publicId}`);
      setOrder(r.data || null);
    } catch { setOrder(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [publicId]);

  async function addPayment() {
    const amt = Math.round(Number(payAmount || 0) * 100) / 100;
    if (!(amt > 0)) return;
    setAddingPayment(true);
    try {
      await api.post(`/admin/sales-orders/${publicId}/payments`,
                        { amount: amt, method: payMethod });
      toast.success("Payment recorded.");
      setPayAmount("");
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Payment failed.");
    } finally { setAddingPayment(false); }
  }

  async function updateStatus(patch) {
    try {
      const r = await api.patch(`/admin/sales-orders/${publicId}/status`, patch);
      setOrder(r.data?.order || order);
      if (r.data?.auto_completed) toast.success("Marked complete.");
      else                        toast.success("Status updated.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Update failed.");
    }
  }

  async function undoLastTransition() {
    try {
      const r = await api.post(`/admin/sales-orders/${publicId}/undo-last-transition`);
      setOrder(r.data?.order || order);
      toast.success("Last transition undone.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Nothing to undo.");
    }
  }

  async function patchShipping(patch) {
    try {
      const r = await api.patch(`/admin/sales-orders/${publicId}/shipping`, patch);
      setOrder(r.data?.order || order);
      toast.success("Shipping updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Update failed.");
    }
  }

  if (loading) return <div className="p-6 text-sm text-[var(--smoke)]">Loading…</div>;
  if (!order)  return <div className="p-6 text-sm text-red-600">Order not found.</div>;

  const total = order.pricing_snapshot?.totals?.total || 0;
  const lines = [
    ...(order.pricing_snapshot?.lines         || []),
    ...(order.pricing_snapshot?.product_lines || []),
    ...(order.pricing_snapshot?.custom_lines  || []),
  ];

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-24"
         data-testid="sales-order-detail">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href="/admin/sales-orders" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Sales Order</div>
          <div className="font-bold truncate">{order.public_id}</div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1120px] p-3 space-y-3"
             data-testid="sales-order-detail-container">
        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white">
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Customer</div>
          <div className="font-semibold">{order.customer?.name || "—"}</div>
          <div className="text-xs text-[var(--smoke)]">
            {order.customer?.email || order.customer?.phone}
          </div>
        </section>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white">
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-2">Items</div>
          <div className="divide-y">
            {lines.map((l, i) => (
              <div key={i} className="py-2 flex text-sm">
                <div className="flex-1 min-w-0 truncate">
                  {l.title || l.description || l.code}
                  <span className="text-[11px] text-[var(--smoke)] ml-1">× {l.qty}</span>
                </div>
                <div className="w-16 text-right font-semibold">{money(l.line_total)}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t flex items-center justify-between">
            <span className="font-semibold text-sm">Total</span>
            <b className="text-[var(--panini-red)]" data-testid="sales-order-detail-total">
              {money(total)}
            </b>
          </div>
        </section>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Payments</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>Amount Received</div>
            <div className="text-right font-semibold" data-testid="sales-order-amount-received">
              {money(order.amount_received ?? order.amount_paid)}
            </div>
            <div>Applied to Order</div>
            <div className="text-right font-semibold" data-testid="sales-order-amount-applied">
              {money(order.amount_applied ?? order.amount_paid)}
            </div>
            {Number(order.total_overpayment || 0) > 0 && (
              <>
                <div>Overpayment</div>
                <div className="text-right font-semibold text-emerald-700"
                     data-testid="sales-order-overpayment">
                  {money(order.total_overpayment)}
                </div>
              </>
            )}
            <div>Balance Due</div>
            <div className="text-right font-bold text-red-600" data-testid="sales-order-balance-due">
              {money(order.balance_due)}
            </div>
            <div>Payment Status</div>
            <div className="text-right" data-testid="sales-order-payment-status">
              <SalesOrderStatusBadge value={order.payment_status || "unpaid"} kind="payment"/>
            </div>
          </div>
          <div className="border-t pt-2 space-y-1">
            {(order.payments || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs"
                   data-testid={`sales-order-payment-${p.id}`}>
                <span>{p.method.toUpperCase()} · {(p.received_at || "").slice(0, 10)}</span>
                <span className="font-semibold">
                  {money(p.amount_received ?? p.amount)}
                  {Number(p.overpayment_amount || 0) > 0 && (
                    <span className="text-emerald-700 ml-1 text-[11px]">
                      (+{money(p.overpayment_amount)} over)
                    </span>
                  )}
                </span>
              </div>
            ))}
            {(!order.payments || order.payments.length === 0) && (
              <div className="text-[11px] text-[var(--smoke)]">No payments recorded.</div>
            )}
          </div>
          {order.sales_order_status !== "cancelled" && (
            <div className="border-t pt-2 space-y-2">
              <div className="text-[10px] uppercase text-[var(--smoke)]">
                Record Payment
                <span className="text-[10px] normal-case text-[var(--smoke)] ml-1">
                  (tenders ≥ balance record overpayment truthfully)
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" min="0" step="0.01" value={payAmount}
                       onChange={(e) => setPayAmount(e.target.value)}
                       placeholder={money(order.balance_due || 0).slice(1)}
                       className="border rounded-xl px-3 py-2 text-sm"
                       data-testid="sales-order-add-payment-amount"/>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                        className="border rounded-xl px-2 py-2 text-sm bg-white"
                        data-testid="sales-order-add-payment-method">
                  {PAYMENTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <button onClick={addPayment} disabled={addingPayment || !(Number(payAmount) > 0)}
                      data-testid="sales-order-add-payment-btn"
                      className={`w-full rounded-2xl py-3 font-bold text-white ${
                        addingPayment || !(Number(payAmount) > 0)
                          ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                      }`}>
                {addingPayment ? "Saving…" : "Record Payment"}
              </button>
            </div>
          )}
        </section>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2"
                 data-testid="sales-order-status-section">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Status</div>
          <div className="grid grid-cols-[90px_1fr] gap-x-2 gap-y-1.5 text-sm items-center">
            <div className="text-[var(--smoke)]">Order</div>
            <div data-testid="sales-order-status">
              <SalesOrderStatusBadge value={order.sales_order_status || "open"} kind="order"/>
              {order._auto_completed_hint && (
                <span className="text-[10px] text-emerald-700 ml-2">auto-completed</span>
              )}
            </div>
            <div className="text-[var(--smoke)]">Payment</div>
            <div>
              <SalesOrderStatusBadge value={order.payment_status || "unpaid"} kind="payment"/>
            </div>
            <div className="text-[var(--smoke)]">Fulfillment</div>
            <div data-testid="sales-order-fulfillment-status">
              <SalesOrderStatusBadge value={_displayFulfillment(order)} kind="fulfillment"/>
            </div>
          </div>

          {/* Contextual action buttons — only valid-for-state actions. */}
          {isEditableOrder(order) && (
            <a href={`/admin/sales-orders?id=${order.public_id}&edit=1`}
               data-testid="sales-order-edit-order-btn"
               className="inline-block mt-1 text-xs font-semibold border-2 border-[var(--ink)] rounded-full px-3 py-1 bg-amber-100 text-slate-800">
              Edit Order
            </a>
          )}
          {order.sales_order_status === "cancelled" ? (
            <div className="pt-2 border-t text-xs text-[var(--smoke)]"
                 data-testid="sales-order-cancelled-notice">
              This order is cancelled. Fulfillment actions are disabled.
              <button onClick={undoLastTransition}
                      data-testid="sales-order-undo-btn"
                      className="ml-2 underline text-slate-700">
                Undo last transition
              </button>
            </div>
          ) : (
            <div className="pt-2 border-t flex flex-wrap gap-2"
                 data-testid="sales-order-actions">
              {_pickupNextAction(order) === "ready_pickup" && (
                <button onClick={() => updateStatus({ fulfillment_status: "ready_pickup" })}
                        data-testid="sales-order-mark-ready-pickup-btn"
                        className="flex-1 min-w-[140px] bg-amber-600 text-white rounded-xl py-2 text-sm font-bold">
                  Mark Ready for Pickup
                </button>
              )}
              {_pickupNextAction(order) === "picked_up" && (
                <button onClick={() => updateStatus({ fulfillment_status: "picked_up" })}
                        data-testid="sales-order-mark-picked-up-btn"
                        className="flex-1 min-w-[140px] bg-emerald-700 text-white rounded-xl py-2 text-sm font-bold">
                  Mark Picked Up
                </button>
              )}
              {_showMarkShippedAction(order) && (
                <button onClick={() => patchShipping({
                          mark_shipped: true,
                          send_shipping_confirmation_email:
                            Boolean(order.customer?.email) && sendShippingEmail,
                        })}
                        data-testid="sales-order-mark-shipped-btn"
                        className="flex-1 min-w-[140px] bg-emerald-700 text-white rounded-xl py-2 text-sm font-bold">
                  Mark Shipped
                </button>
              )}
              <button onClick={undoLastTransition}
                      data-testid="sales-order-undo-btn"
                      className="min-w-[120px] bg-slate-200 text-slate-800 rounded-xl py-2 text-sm font-semibold">
                Undo Last
              </button>
              {order.sales_order_status !== "completed" && (
                <button onClick={() => {
                          if (window.confirm("Cancel this sales order? This disables all fulfillment actions."))
                            updateStatus({ sales_order_status: "cancelled" });
                        }}
                        data-testid="sales-order-cancel-btn"
                        className="min-w-[120px] bg-slate-600 text-white rounded-xl py-2 text-sm font-semibold">
                  Cancel Order
                </button>
              )}
            </div>
          )}
        </section>

        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Shipping</div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Carrier (USPS, UPS…)" defaultValue={order.shipping?.carrier || ""}
                   onBlur={(e) => e.target.value !== (order.shipping?.carrier || "") &&
                                     patchShipping({ carrier: e.target.value })}
                   className="border rounded-xl px-3 py-2 text-sm"
                   data-testid="sales-order-carrier"/>
            <input placeholder="Tracking number" defaultValue={order.shipping?.tracking_number || ""}
                   onBlur={(e) => e.target.value !== (order.shipping?.tracking_number || "") &&
                                     patchShipping({ tracking_number: e.target.value })}
                   className="border rounded-xl px-3 py-2 text-sm"
                   data-testid="sales-order-tracking"/>
          </div>
          {order.shipping?.address && (
            <div className="text-[11px] text-[var(--smoke)]" data-testid="sales-order-ship-address">
              Ship to: {order.shipping.address.recipient_name}, {order.shipping.address.address_line_1}
              {order.shipping.address.address_line_2 ? `, ${order.shipping.address.address_line_2}` : ""},
              {" "}{order.shipping.address.city}, {order.shipping.address.state_region} {order.shipping.address.postal_code}
            </div>
          )}
          {order.shipping?.shipped_at && (
            <div className="text-[11px] text-emerald-700 font-semibold">
              Shipped {String(order.shipping.shipped_at).slice(0, 10)}
            </div>
          )}
          {order.fulfillment_status !== "shipped" && order.sales_order_status !== "cancelled" && order.fulfillment_intent === "shipping" && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm select-none"
                     data-testid="sales-order-send-shipping-email-label">
                <input type="checkbox"
                       checked={Boolean(order.customer?.email) && sendShippingEmail}
                       disabled={!order.customer?.email}
                       onChange={(e) => setSendShippingEmail(e.target.checked)}
                       data-testid="sales-order-send-shipping-email-checkbox"
                       className="w-4 h-4"/>
                <span>
                  Email shipping confirmation to customer
                  {!order.customer?.email && (
                    <span className="text-[11px] text-[var(--smoke)] ml-1">
                      (no email on file)
                    </span>
                  )}
                </span>
              </label>
              {/* Mark Shipped is rendered inside the Status action row
                  above — this block only exposes the email-opt-in copy. */}
            </div>
          )}
          {(order.emails?.confirmation?.sent_at || order.emails?.shipping?.sent_at) && (
            <div className="border-t pt-2 space-y-1 text-[11px]"
                 data-testid="sales-order-email-status">
              {order.emails?.confirmation?.sent_at && (
                <div data-testid="sales-order-email-status-confirmation">
                  <span className="font-semibold">Order confirmation emailed</span>
                  <span className="text-[var(--smoke)] ml-1">
                    {String(order.emails.confirmation.sent_at).slice(0, 10)}
                  </span>
                </div>
              )}
              {order.emails?.shipping?.sent_at && (
                <div data-testid="sales-order-email-status-shipping">
                  <span className="font-semibold">Shipping confirmation emailed</span>
                  <span className="text-[var(--smoke)] ml-1">
                    {String(order.emails.shipping.sent_at).slice(0, 10)}
                  </span>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
